import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../App";
import { formatAdminRole } from "../accessControl";
import { getInlineLoader } from "../Helpers";
import {
  MdCalendarToday,
  MdDirectionsBike,
  MdOutlinePayments,
  MdPictureAsPdf,
  MdHistory,
} from "react-icons/md";
import CollectionsReport from "../reports/CollectionsReport";
import PortfolioReport from "../reports/PortfolioReport";

const REPORT_OPTIONS = [
  { value: "customers", label: "1. Customers Report" },
  { value: "motorcycles", label: "2. Motorcycles Register" },
  { value: "sales", label: "3. Sales Performance" },
  { value: "debts", label: "4. Debt Accounts Ledger" },
  { value: "payments", label: "5. Payments Posted" },
];

const INTERVAL_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

const DEFAULT_FILTERS = {
  customers: "all",
  motorcycles: "all",
  sales: "all",
  debts: "all",
  payments: "all",
};

const DEFAULT_GROUPING = {
  customers: "none",
  motorcycles: "type",
  sales: "saleType",
  debts: "status",
  payments: "none",
};

export default function ReportsHome() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [activeReport, setActiveReport] = useState("customers");
  const [interval, setInterval] = useState("30d");
  const [category, setCategory] = useState(DEFAULT_FILTERS.customers);
  const [groupBy, setGroupBy] = useState(DEFAULT_GROUPING.customers);
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const reportRef = useRef();

  async function init() {
    setReady(false);
    await Promise.all([
      appContext.getAllAdmins(),
      appContext.getAllCustomers(),
      appContext.getAllSales(),
      appContext.getAllDebts(),
      appContext.getAllPayments(),
      appContext.getAllMotorcycles(),
      appContext.getAvailableResale ? appContext.getAvailableResale() : Promise.resolve(),
    ]);
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    setCategory(DEFAULT_FILTERS[activeReport] || "all");
    setGroupBy(DEFAULT_GROUPING[activeReport] || "none");
    setSelectedCustomer("all");
  }, [activeReport]);

  const admins = useMemo(() => appContext.allAdmins || [], [appContext.allAdmins]);
  const customers = useMemo(() => appContext.allCustomers || [], [appContext.allCustomers]);
  const sales = useMemo(() => appContext.allSales || [], [appContext.allSales]);
  const debts = useMemo(() => appContext.allDebts || [], [appContext.allDebts]);
  const payments = useMemo(() => appContext.allPayments || [], [appContext.allPayments]);
  const motorcycles = useMemo(() => appContext.allMotorcycles || [], [appContext.allMotorcycles]);
  const resales = useMemo(() => appContext.allResale || appContext.resaleMotorcycles || [], [appContext.allResale, appContext.resaleMotorcycles]);

  const adminLookup = useMemo(() => {
    return admins.reduce((acc, item) => {
      const key = String(item.id || item.uuid || "").toLowerCase();
      if (key) acc[key] = item.fullname || "System User";
      return acc;
    }, {});
  }, [admins]);

  const customerOptions = useMemo(() => {
    return customers
      .map((item) => ({
        value: String(item.uuid || item.id || "").toLowerCase(),
        label: item.fullname || "Unnamed Customer",
      }))
      .filter((item) => item.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  const range = useMemo(() => getDateRange(interval, startDate, endDate), [interval, startDate, endDate]);
  const intervalLabel = useMemo(() => getIntervalLabel(interval, startDate, endDate), [interval, startDate, endDate]);
  const generatedAt = useMemo(() => new Date().toLocaleString(), []);
  const issuedBy = appContext.currentUserData?.fullname || "System";
  const issuedRole = formatAdminRole(appContext.currentUserData?.role);

  // --- DATA PIPELINES ---
  const enrichedCustomers = useMemo(() => {
    return customers.map((item) => {
      const cSales = sales.filter((s) => String(s.customerID).toLowerCase() === String(item.uuid).toLowerCase());
      const cDebts = debts.filter((d) => String(d.customerID).toLowerCase() === String(item.uuid).toLowerCase());
      return {
        ...item,
        reportDate: getRecordDate(item),
        displayDate: formatDisplayDate(getRecordDate(item)),
        totalPurchases: cSales.length,
        activeDebtsCount: cDebts.filter((d) => d.status === "active").length,
        outstandingBalance: cDebts.reduce((sum, d) => sum + Number(d.balance || 0), 0),
      };
    });
  }, [customers, sales, debts]);

  const enrichedMotorcycles = useMemo(() => {
    const stockUnits = motorcycles.map((item) => {
      const total = parseInt(item.totalReceived) || 0;
      const allocated = parseInt(item.allocatedCount) || 0;
      const available = Math.max(total - allocated, 0);
      return {
        ...item,
        model: `${item.model || "Unknown Model"} (${available} units available)`,
        bikeType: "STOCK",
        status: item.status || "available",
        reportDate: getRecordDate(item),
        displayDate: formatDisplayDate(getRecordDate(item)),
      };
    });

    const resaleUnits = resales.map((item) => ({
      ...item,
      model: item.motorcycleModel || "Unknown Model",
      bikeType: "RESALE",
      status: item.resaleStatus || "available",
      reportDate: getRecordDate(item),
      displayDate: formatDisplayDate(getRecordDate(item)),
    }));

    return [...stockUnits, ...resaleUnits];
  }, [motorcycles, resales]);

  const enrichedSales = useMemo(() => {
    return sales.map((item) => ({
      ...item,
      reportDate: getRecordDate(item),
      displayDate: formatDisplayDate(getRecordDate(item)),
      customerName: item.fullname || "Unknown Customer",
      officerName: item.officerName || adminLookup[String(item.assignedTo).toLowerCase()] || "System",
      displayType: item.resaleUuid ? "Resale" : "Stock",
    }));
  }, [sales, adminLookup]);

  const enrichedDebts = useMemo(() => {
    return debts.map((item) => {
      const duration = item.durationMonths || item.loanDuration || "16";
      return {
        ...item,
        customerKey: String(item.customerID || "").toLowerCase(),
        customerName: item.fullname || "Unknown Customer",
        customerPhone: item.phone || "",
        officerName: item.officerName || adminLookup[String(item.assignedTo).toLowerCase()] || "System",
        model: `${item.model || "Motorcycle"}`,
        reportDate: getRecordDate(item),
        displayDate: formatDisplayDate(getRecordDate(item)),
      };
    });
  }, [debts, adminLookup]);

  const enrichedPayments = useMemo(() => {
    return payments.map((item) => {
      const matchedDebt = debts.find(d => String(d.uuid).toLowerCase() === String(item.debtID).toLowerCase() || String(d.id).toLowerCase() === String(item.debtID).toLowerCase());
      return {
        ...item,
        customerKey: String(item.customerID || "").toLowerCase(),
        customerName: item.fullname || "Unknown Customer",
        officerName: matchedDebt?.officerName || adminLookup[String(matchedDebt?.assignedTo).toLowerCase()] || "System",
        reportDate: getRecordDate(item),
        displayDate: formatDisplayDate(getRecordDate(item)),
        initialLoanAmount: 0, 
      };
    });
  }, [payments, debts, adminLookup]);

  // --- FILTERS EXECUTIONS ---
  const filteredCustomers = useMemo(() => {
    return enrichedCustomers
      .filter((item) => isWithinRange(item.reportDate, range))
      .filter((item) => selectedCustomer === "all" || String(item.uuid).toLowerCase() === selectedCustomer)
      .sort((a, b) => b.outstandingBalance - a.outstandingBalance);
  }, [enrichedCustomers, range, selectedCustomer]);

  const filteredMotorcycles = useMemo(() => {
    return enrichedMotorcycles
      .filter((item) => isWithinRange(item.reportDate, range))
      .filter((item) => {
        if (category === "stock") return item.bikeType === "STOCK";
        if (category === "resale") return item.bikeType === "RESALE";
        return true;
      })
      .sort((a, b) => sortByDateDesc(a.reportDate, b.reportDate));
  }, [enrichedMotorcycles, range, category]);

  const filteredSales = useMemo(() => {
    return enrichedSales
      .filter((item) => isWithinRange(item.reportDate, range))
      .filter((item) => category === "all" || item.saleType === category)
      .filter((item) => selectedCustomer === "all" || String(item.customerID).toLowerCase() === selectedCustomer)
      .sort((a, b) => sortByDateDesc(a.reportDate, b.reportDate));
  }, [enrichedSales, range, category, selectedCustomer]);

  const filteredDebts = useMemo(() => {
    return enrichedDebts
      .filter((item) => isWithinRange(item.reportDate, range))
      .filter((item) => category === "all" || item.status === category)
      .filter((item) => selectedCustomer === "all" || item.customerKey === selectedCustomer)
      .sort((a, b) => sortByDateDesc(a.reportDate, b.reportDate));
  }, [enrichedDebts, range, category, selectedCustomer]);

  const filteredPayments = useMemo(() => {
    return enrichedPayments
      .filter((item) => isWithinRange(item.reportDate, range))
      .filter((item) => selectedCustomer === "all" || item.customerKey === selectedCustomer)
      .sort((a, b) => sortByDateDesc(a.reportDate, b.reportDate));
  }, [enrichedPayments, range, selectedCustomer]);

  const handleDownloadPDF = async () => {
    const html2pdf = (await import("html2pdf.js")).default;
    const opt = {
      margin: 0.35,
      filename: `Report_${activeReport}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 1.8, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "landscape" },
    };
    html2pdf().set(opt).from(reportRef.current).save();
  };

  const activeFilterOptions = getCategoryOptions(activeReport);
  const activeCategoryLabel = activeFilterOptions.find((i) => i.value === category)?.label;

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <div className="container">
      <div className="app-page">
        <div className="app-page-hero">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h6 className="main-section-title">Operational Reports Manager</h6>
              <p className="app-page-subtitle">Consolidated operational ledgers for audit trails, sales movement, and active debt balances.</p>
            </div>
            <button className="btn btn-outline-danger btn-rounded app-cta-btn" onClick={handleDownloadPDF}>
              <MdPictureAsPdf size={18} /> Download PDF
            </button>
          </div>
        </div>

        {/* Dynamic Category Tabs */}
        <div className="detail-actions-card">
          <div className="d-flex gap-2 flex-wrap">
            {REPORT_OPTIONS.map((item) => (
              <button
                key={item.value}
                className={`btn ${activeReport === item.value ? "btn-dark" : "btn-light"}`}
                onClick={() => setActiveReport(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Global Toolbar Controllers */}
        <div className="list-shell mt-4">
          <div className="detail-actions-card">
            <div className="row">
              <div className="col-md-4 mt-2">
                <label className="form-label mb-1">Interval Frame</label>
                <select className="form-control modern-filter" value={interval} onChange={(e) => setInterval(e.target.value)}>
                  {INTERVAL_OPTIONS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </div>

              {activeFilterOptions.length > 0 && (
                <div className="col-md-4 mt-2">
                  <label className="form-label mb-1">Sub-Category Selection</label>
                  <select className="form-control modern-filter" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {activeFilterOptions.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
              )}

              {activeReport !== "motorcycles" && (
                <div className="col-md-4 mt-2">
                  <label className="form-label mb-1">Filter Customer</label>
                  <select className="form-control modern-filter" value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
                    <option value="all">All Customers</option>
                    {customerOptions.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
              )}

              {interval === "custom" && (
                <div className="col-12 row mt-2 px-0 mx-0 border-top pt-2">
                  <div className="col-md-3">
                    <label className="form-label mb-1">Start Date</label>
                    <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label mb-1">End Date</label>
                    <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap mt-3 text-muted small border-top pt-2">
              <span><MdCalendarToday size={14} /> Scope: {intervalLabel}</span>
              <span><MdHistory size={14} /> Filter Index: {activeCategoryLabel || "System Base"}</span>
            </div>
          </div>
        </div>

        {/* Dynamic Canvas Node */}
        <div ref={reportRef} className="report-content-wrapper report-print-surface mt-4">
          {activeReport === "customers" && (
            <PortfolioReport
              title="Customer Profiles & Portfolio Balances"
              subtitle="Consolidated active profiles matching ledger parameters."
              periodLabel={intervalLabel}
              categoryLabel="Profiles Summary"
              groups={[]}
              rows={filteredCustomers}
              groupBy="none"
              issuedBy={issuedBy}
              issuedRole={issuedRole}
              generatedAt={generatedAt}
              emptyMessage="No profiles located inside parameters."
              tableColumns={[
                { key: "displayDate", label: "Date Added" },
                { key: "fullname", label: "Fullname" },
                { key: "phone", label: "Phone" },
                { key: "totalPurchases", label: "Total Sales" },
                { key: "activeDebtsCount", label: "Active Loans" },
                { key: "outstandingBalance", label: "Total Outstanding Balance", type: "money", align: "end" },
              ]}
              getFooterValues={(rows) => [
                "TOTAL", "", "", 
                rows.reduce((sum, item) => sum + Number(item.totalPurchases || 0), 0).toLocaleString(),
                rows.reduce((sum, item) => sum + Number(item.activeDebtsCount || 0), 0).toLocaleString(),
                rows.reduce((sum, item) => sum + Number(item.outstandingBalance || 0), 0).toLocaleString(),
              ]}
            />
          )}

          {activeReport === "motorcycles" && (
            <PortfolioReport
              title="Motorcycle Register (Stock & Resale Inventory)"
              subtitle="Relational model tracking across new configurations and return profiles."
              periodLabel={intervalLabel}
              categoryLabel={activeCategoryLabel}
              groups={[]}
              rows={filteredMotorcycles}
              groupBy="none"
              issuedBy={issuedBy}
              issuedRole={issuedRole}
              generatedAt={generatedAt}
              emptyMessage="No storage configurations match current search paths."
              tableColumns={[
                { key: "displayDate", label: "Date" },
                { key: "model", label: "Model" },
                { key: "bikeType", label: "Stock/ Resale" },
                { key: "status", label: "Status" },
              ]}
              getFooterValues={(rows) => [
                `TOTAL: ${rows.length} Units`, "", "", "",
              ]}
            />
          )}

          {activeReport === "sales" && (
            <PortfolioReport
              title="Sales Operations Ledger"
              subtitle="Audit trail processing tracking across procurement classifications."
              periodLabel={intervalLabel}
              categoryLabel={activeCategoryLabel}
              groups={[]}
              rows={filteredSales}
              groupBy="none"
              issuedBy={issuedBy}
              issuedRole={issuedRole}
              generatedAt={generatedAt}
              emptyMessage="No operational completions matched target scope."
              tableColumns={[
                { key: "displayDate", label: "Execution Date" },
                { key: "customerName", label: "Buyer Fullname" },
                { key: "model", label: "Model" },
                { key: "saleType", label: "Sale Type" },
                { key: "displayType", label: "STOCK/RESALE" },
                { key: "price", label: "Value", type: "money", align: "end" },
              ]}
              getFooterValues={(rows) => [
                "TOTAL", "", "", "", "",
                rows.reduce((sum, item) => sum + Number(item.price || 0), 0).toLocaleString(),
              ]}
            />
          )}

          {activeReport === "debts" && (
            <PortfolioReport
              title="Debt Records"
              subtitle="Outstanding lifecycle analysis tracking profiles across active financing pools."
              periodLabel={intervalLabel}
              categoryLabel={activeCategoryLabel}
              groups={[]}
              rows={filteredDebts}
              groupBy="none"
              issuedBy={issuedBy}
              issuedRole={issuedRole}
              generatedAt={generatedAt}
              emptyMessage="No asset files matched active parameter blocks."
              tableColumns={[
                { key: "displayDate", label: "Date" },
                { key: "customerName", label: "Fullname" },
                { key: "model", label: "MODEL" },
                { key: "officerName", label: "Loan Officer" },
                { key: "status", label: "Status" },
                { key: "totalAmount", label: "LOAN AMOUNT", type: "money", align: "end" },
                { key: "balance", label: "BALANCE", type: "money", align: "end" },
              ]}
              getFooterValues={(rows) => [
                "TOTAL", "", "", "", "",
                rows.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0).toLocaleString(),
                rows.reduce((sum, item) => sum + Number(item.balance || 0), 0).toLocaleString(),
              ]}
            />
          )}

          {activeReport === "payments" && (
            <CollectionsReport
              title="Payment Records"
              subtitle="Incremental breakdown entries processed across financing operations."
              intervalLabel={intervalLabel}
              periodLabel={intervalLabel}
              categoryLabel="Cash Book Stream"
              groups={[]}
              rows={filteredPayments}
              groupBy="none"
              issuedBy={issuedBy}
              issuedRole={issuedRole}
              generatedAt={generatedAt}
              totals={{
                totalCollected: filteredPayments.reduce((sum, item) => sum + Number(item.amount || 0), 0),
                totalInitialLoan: 0, 
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function getCategoryOptions(report) {
  if (report === "motorcycles") {
    return [
      { value: "all", label: "All Motorcycles" },
      { value: "stock", label: "Stock Units" },
      { value: "resale", label: "Resale Units" },
    ];
  }
  if (report === "sales") {
    return [
      { value: "all", label: "All Sales" },
      { value: "cash", label: "Cash Sale" },
      { value: "loan", label: "Loan Sale" },
    ];
  }
  if (report === "debts") {
    return [
      { value: "all", label: "All Loans" },
      { value: "active", label: "Active Loans" },
      { value: "cleared", label: "Cleared Loans" },
      { value: "cancelled", label: "Cancelled Loans" },
    ];
  }
  return [{ value: "all", label: "All Records" }];
}

function getGroupingOptions(report) {
  return [{ value: "none", label: "Flat Structural Index Ledger" }];
}

function getRecordDate(item) {
  return parseFlexibleDate(item?.dateCreated || item?.date || null);
}

function getDateRange(interval, startDate, endDate) {
  const today = new Date();
  const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
  const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);

  if (interval === "today") return { start: dayStart, end: dayEnd };
  if (interval === "7d") {
    const start = new Date(dayStart);
    start.setDate(start.getDate() - 6);
    return { start, end: dayEnd };
  }
  if (interval === "30d") {
    const start = new Date(dayStart);
    start.setDate(start.getDate() - 29);
    return { start, end: dayEnd };
  }
  if (interval === "this_month") {
    return { start: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0), end: dayEnd };
  }
  if (interval === "custom") {
    return {
      start: startDate ? new Date(`${startDate}T00:00:00`) : null,
      end: endDate ? new Date(`${endDate}T23:59:59.999`) : null,
    };
  }
  return { start: null, end: null };
}

function isWithinRange(value, range) {
  if (!value) return true;
  const date = parseFlexibleDate(value);
  if (Number.isNaN(date.getTime())) return true;
  if (range.start && date < range.start) return false;
  if (range.end && date > range.end) return false;
  return true;
}

function sortByDateDesc(first, second) {
  return parseFlexibleDate(second).getTime() - parseFlexibleDate(first).getTime();
}

function parseFlexibleDate(value) {
  if (!value && value !== 0) return new Date("invalid");
  if (value instanceof Date) return value;
  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const num = Number(value);
    return new Date(String(Math.abs(num)).length <= 10 ? num * 1000 : num);
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  return new Date(value);
}

function formatDisplayDate(value) {
  const date = parseFlexibleDate(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function getIntervalLabel(interval, startDate, endDate) {
  if (interval === "today") return "Today";
  if (interval === "7d") return "Last 7 days";
  if (interval === "30d") return "Last 30 days";
  if (interval === "this_month") return "This month";
  if (interval === "custom") {
    if (startDate && endDate) return `${startDate} to ${endDate}`;
    if (startDate) return `From ${startDate}`;
    if (endDate) return `Until ${endDate}`;
    return "Custom range";
  }
  return "All time";
}

function getReportSubtitle(report, categoryLabel) {
  if (report === "collections") {
    return `Collection ledger for ${String(categoryLabel || "selected records").toLowerCase()} with payment summations at the end of each ledger.`;
  }
  if (report === "motorcycles") {
    return `Motorcycle register for ${String(categoryLabel || "selected models").toLowerCase()} showing stock movement and sales totals.`;
  }
  if (report === "resale") {
    return `Returned asset ledger breakdown for tracking physical registration profiles and assignment allocations.`;
  }
  return `Loan portfolio ledger for ${String(categoryLabel || "selected records").toLowerCase()} with balances and opening loan values.`;
}